import React, { useEffect, useState } from "react";
import { api, authHeaders, getToken } from "@/lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { BellPlus, Loader2, Trash2, MapPin } from "lucide-react";
import { toast } from "sonner";
import { useLocationCtx } from "@/context/LocationContext";
import { CatIcon } from "@/lib/catIcons";
import { Switch } from "@/components/ui/switch";

const DISTANCES = [2, 5, 10, 20, 50];
const DISCOUNTS = [0, 10, 20, 30, 50];

// "Avvisami quando…" — client saves alert preferences; matching new offers trigger a notification.
export default function AlertMeSheet({ open, onOpenChange, macros, onNeedAuth }) {
  const { loc } = useLocationCtx();
  const [alerts, setAlerts] = useState([]);
  const [cats, setCats] = useState([]);
  const [dist, setDist] = useState(10);
  const [minDisc, setMinDisc] = useState(0);
  const [city, setCity] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => {
    if (!getToken("client")) return;
    api.get("/client/alerts", { headers: authHeaders("client") }).then((r) => setAlerts(r.data.alerts || [])).catch(() => {});
  };
  useEffect(() => { if (open) load(); }, [open]);

  const toggle = (id) => setCats((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const save = async () => {
    if (!getToken("client")) { onOpenChange(false); onNeedAuth && onNeedAuth(); return; }
    setSaving(true);
    try {
      await api.post("/client/alerts", {
        categories: cats, max_distance_km: dist, min_discount: minDisc,
        city: city.trim(), lat: loc?.lat, lng: loc?.lng,
      }, { headers: authHeaders("client") });
      toast.success("Avviso creato! Ti avviseremo appena esce un'offerta adatta.");
      setCats([]); setCity(""); setMinDisc(0); setDist(10);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    try { await api.delete(`/client/alerts/${id}`, { headers: authHeaders("client") }); load(); } catch (_) {}
  };

  const toggleActive = async (a) => {
    try {
      await api.patch(`/client/alerts/${a.alert_id}`, { active: !a.active }, { headers: authHeaders("client") });
      setAlerts((s) => s.map((x) => (x.alert_id === a.alert_id ? { ...x, active: !x.active } : x)));
    } catch (_) { toast.error("Errore"); }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="rounded-t-3xl max-w-3xl mx-auto max-h-[88vh] overflow-y-auto" data-testid="alertme-sheet">
        <SheetHeader className="text-left">
          <SheetTitle className="text-xl font-extrabold flex items-center gap-2"><BellPlus className="h-5 w-5 text-[#E63946]" /> Avvisami quando…</SheetTitle>
        </SheetHeader>
        <p className="text-sm text-slate-500 mt-1">Scegli le tue preferenze: ti avviseremo appena un negozio pubblica un'offerta che fa per te.</p>
        <div className="mt-3 rounded-xl bg-[#FFF1F2] border border-[#FFD9DE] p-3" data-testid="alertme-intro">
          <p className="text-sm text-slate-600"><span className="font-bold text-[#B91C3B]">A cosa serve?</span> Crei un avviso con le tue preferenze (categorie, zona, distanza, sconto) e ricevi una notifica appena esce un'offerta che le rispetta. Puoi crearne più d'uno, metterli in pausa o eliminarli quando vuoi.</p>
        </div>

        <div className="py-4 space-y-5">
          <div>
            <p className="text-sm font-bold text-slate-700 mb-2">Categorie (lascia vuoto = tutte)</p>
            {(macros || []).map((m) => (
              <div key={m.id} className="mb-2">
                <p className="text-xs font-bold text-slate-400 mb-1">{m.label}</p>
                <div className="flex flex-wrap gap-1.5">
                  {m.tags.map((t) => (
                    <button key={t.id} type="button" data-testid={`alert-cat-${t.id}`} onClick={() => toggle(t.id)}
                      className={`px-2.5 py-1 rounded-full text-xs font-semibold flex items-center gap-1 ${cats.includes(t.id) ? "kn-gradient text-white" : "bg-slate-100 text-slate-600"}`}>
                      <CatIcon id={t.id} className="h-3 w-3" />
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div>
            <p className="text-sm font-bold text-slate-700 mb-2">Zona / città (facoltativo)</p>
            <Input data-testid="alert-city" value={city} onChange={(e) => setCity(e.target.value)} placeholder="Es. Pesaro centro" className="h-11 rounded-xl" />
          </div>

          <div>
            <p className="text-sm font-bold text-slate-700 mb-2 flex items-center gap-1"><MapPin className="h-4 w-4" /> Distanza massima</p>
            <div className="flex gap-2">
              {DISTANCES.map((d) => (
                <button key={d} type="button" data-testid={`alert-dist-${d}`} onClick={() => setDist(d)}
                  className={`flex-1 py-2 rounded-xl text-sm font-bold ${dist === d ? "kn-gradient text-white" : "bg-slate-100 text-slate-600"}`}>{d} km</button>
              ))}
            </div>
          </div>

          <div>
            <p className="text-sm font-bold text-slate-700 mb-2">Sconto minimo</p>
            <div className="flex gap-2">
              {DISCOUNTS.map((d) => (
                <button key={d} type="button" data-testid={`alert-disc-${d}`} onClick={() => setMinDisc(d)}
                  className={`flex-1 py-2 rounded-xl text-sm font-bold ${minDisc === d ? "kn-gradient text-white" : "bg-slate-100 text-slate-600"}`}>{d === 0 ? "Tutti" : `${d}%+`}</button>
              ))}
            </div>
          </div>

          <Button onClick={save} disabled={saving} data-testid="alert-save" className="w-full h-12 rounded-xl font-bold gap-2">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <BellPlus className="h-4 w-4" />} Crea avviso
          </Button>

          {alerts.length > 0 && (
            <div className="space-y-2">
              <p className="text-sm font-bold text-slate-700">I tuoi avvisi ({alerts.length})</p>
              {alerts.map((a) => (
                <div key={a.alert_id} data-testid={`alert-row-${a.alert_id}`} className={`kn-card p-3 flex items-center gap-3 ${a.active ? "" : "opacity-60"}`}>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-slate-800 truncate">
                      {a.category_labels.length ? a.category_labels.join(", ") : "Tutte le categorie"}
                    </p>
                    <p className="text-xs text-slate-400">Entro {a.max_distance_km} km{a.min_discount ? ` · sconto ${a.min_discount}%+` : ""}{a.city ? ` · ${a.city}` : ""}{a.active ? "" : " · in pausa"}</p>
                  </div>
                  <Switch checked={a.active} onCheckedChange={() => toggleActive(a)} data-testid={`alert-toggle-${a.alert_id}`} />
                  <button onClick={() => remove(a.alert_id)} data-testid={`alert-del-${a.alert_id}`} className="h-9 w-9 rounded-lg flex items-center justify-center text-red-600 hover:bg-red-50 shrink-0"><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
